"use client";

// Data layer Popup Builder (mig 194). Helper murni & tipe ada di
// src/lib/popups.ts. Halaman admin (/admin/popups) otomatis memakai sesi
// admin via scope.ts; storefront memakai scope "store" eksplisit (PopupDisplay
// ter-mount global, termasuk saat berada di /admin — di sana ia no-op).

import { createBrowserSupabase } from "@/lib/supabase/browser";
import { getClientStoreId } from "@/lib/supabase/storeContextClient";
import { withStorePrefix } from "@/lib/supabase/storagePaths";
import {
  duplicatePopupDraft,
  mapPopupRow,
  type Popup,
  type PopupDraft,
  type PopupRow,
} from "@/lib/popups";

const SELECT =
  "id, name, is_active, image_url, image_link, delay_seconds, blocks, audience, group_ids, pages, frequency, starts_at, ends_at, sort_order, created_at";

/** Daftar semua popup (admin melihat aktif & nonaktif), urut prioritas. */
export async function fetchPopupsAdmin(): Promise<Popup[]> {
  const db = createBrowserSupabase();
  if (!db) return [];

  const { data, error } = await db
    .from("popups")
    .select(SELECT)
    .order("sort_order", { ascending: true })
    .order("created_at", { ascending: false });

  if (error) throw new Error(error.message);
  return (data as PopupRow[] | null)?.map(mapPopupRow) ?? [];
}

/**
 * Popup AKTIF saja — dipakai storefront (RLS mengizinkan publik baca baris
 * aktif). Evaluasi halaman/audiens/frekuensi dilakukan di klien.
 */
export async function fetchActivePopups(): Promise<Popup[]> {
  const db = createBrowserSupabase("store");
  if (!db) return [];

  // MULTI-TENANT — FAIL-CLOSED. Pengunjung anon lolos cabang escape RLS
  // (`current_store_id() is null`, keputusan A2), jadi tanpa filter store_id
  // query ini memulangkan popup AKTIF SELURUH TOKO.
  //
  // ⛔ JANGAN kembalikan ke "storeId null → tanpa filter". Bentuk fail-open itu
  // adalah bug live: di apex platform `kelolify.com` tak ada toko yang
  // ter-resolusi (RPC `resolve_store_public` memang memulangkan NULL untuk host
  // platform) → filter dilewati → popup milik tenant (zzhomey) tampil di
  // halaman jualan platform. Subdomain tenant lain tidak kebagian justru karena
  // di sana filternya jalan — itulah sebabnya kebocoran ini hanya terlihat di
  // apex. Halaman platform bahkan SENGAJA tanpa akses database (lihat
  // `src/app/platform/page.tsx`); PopupDisplay ter-mount global lewat
  // <Providers> sehingga menembus niat itu.
  //
  // Toko tak ter-resolusi ≠ "pakai toko mana saja" (konvensi CLAUDE.md, PR
  // #571) — cermin `fetchActivePromoCampaigns`. Paling buruk pengunjung tak
  // melihat popup; ia tak pernah melihat popup toko lain.
  const storeId = getClientStoreId();
  if (!storeId) return [];

  const { data, error } = await db
    .from("popups")
    .select(SELECT)
    .eq("is_active", true)
    .eq("store_id", storeId)
    .order("sort_order", { ascending: true })
    .order("created_at", { ascending: false });
  if (error) throw new Error(error.message);
  return (data as PopupRow[] | null)?.map(mapPopupRow) ?? [];
}

/** Satu popup by id (editor admin). null bila tidak ditemukan. */
export async function fetchPopupById(id: string): Promise<Popup | null> {
  const db = createBrowserSupabase();
  if (!db) return null;

  const { data, error } = await db
    .from("popups")
    .select(SELECT)
    .eq("id", id)
    .maybeSingle();

  if (error) throw new Error(error.message);
  return data ? mapPopupRow(data as PopupRow) : null;
}

/** Simpan popup (insert bila `id` kosong, update bila ada). */
export async function savePopup(
  draft: PopupDraft,
  id?: string
): Promise<void> {
  const db = createBrowserSupabase();
  if (!db) throw new Error("Supabase belum siap.");

  const payload = {
    name: draft.name.trim(),
    is_active: draft.isActive,
    image_url: draft.imageUrl,
    image_link: draft.imageUrl ? draft.imageLink : null,
    delay_seconds: Math.max(0, Math.floor(draft.delaySeconds || 0)),
    blocks: draft.blocks,
    audience: draft.audience,
    group_ids: draft.audience === "groups" ? draft.groupIds : [],
    pages: draft.pages,
    frequency: draft.frequency,
    starts_at: draft.startsAt,
    ends_at: draft.endsAt,
  };

  if (id) {
    const { error } = await db.from("popups").update(payload).eq("id", id);
    if (error) throw new Error(error.message);
  } else {
    const { error } = await db.from("popups").insert(payload);
    if (error) throw new Error(error.message);
  }
}

/** Aktif/nonaktifkan popup tanpa menghapusnya. */
export async function setPopupActive(
  id: string,
  isActive: boolean
): Promise<void> {
  const db = createBrowserSupabase();
  if (!db) throw new Error("Supabase belum siap.");

  const { error } = await db
    .from("popups")
    .update({ is_active: isActive })
    .eq("id", id);
  if (error) throw new Error(error.message);
}

/**
 * Simpan urutan prioritas popup: tulis `sort_order` tiap baris = posisi
 * indeksnya di `orderedIds` (pola persistExpeditionOrder).
 */
export async function persistPopupOrder(orderedIds: string[]): Promise<void> {
  const db = createBrowserSupabase();
  if (!db) throw new Error("Supabase belum siap.");

  const results = await Promise.all(
    orderedIds.map((id, index) =>
      db.from("popups").update({ sort_order: index }).eq("id", id)
    )
  );
  const failed = results.find((r) => r.error);
  if (failed?.error) throw new Error(failed.error.message);
}

/** Duplikat popup (buat salinan nonaktif ber-nama "… (salinan)"). */
export async function duplicatePopup(id: string): Promise<void> {
  const popup = await fetchPopupById(id);
  if (!popup) throw new Error("Popup tidak ditemukan.");
  await savePopup(duplicatePopupDraft(popup));
}

/** Hapus popup. */
export async function deletePopup(id: string): Promise<void> {
  const db = createBrowserSupabase();
  if (!db) throw new Error("Supabase belum siap.");

  const { error } = await db.from("popups").delete().eq("id", id);
  if (error) throw new Error(error.message);
}

// ── Pemilih link (tujuan tombol/gambar) ─────────────────────────────────────

export type PopupProductOption = { name: string; slug: string; brand: string | null };

/** Cari produk aktif (nama/slug) untuk pemilih tujuan link. */
export async function searchPopupProducts(
  query: string
): Promise<PopupProductOption[]> {
  const db = createBrowserSupabase();
  if (!db) return [];
  const q = query.trim();
  let pq = db
    .from("products")
    .select("name, slug, brand")
    .eq("is_active", true)
    .order("name", { ascending: true })
    .limit(20);
  if (q) {
    const safe = q.replace(/[%,()]/g, " ");
    pq = pq.ilike("name", `%${safe}%`);
  }
  const { data, error } = await pq;
  if (error) throw new Error(error.message);
  return ((data as Record<string, unknown>[] | null) ?? [])
    .filter((r) => typeof r.slug === "string" && r.slug)
    .map((r) => ({
      name: typeof r.name === "string" ? r.name : "",
      slug: String(r.slug),
      brand: (r.brand as string) ?? null,
    }));
}

/** Nama kategori aktif untuk pemilih tujuan link. */
export async function fetchPopupCategories(): Promise<string[]> {
  const db = createBrowserSupabase();
  if (!db) return [];
  const { data, error } = await db
    .from("product_categories")
    .select("name")
    .eq("is_active", true)
    .order("name", { ascending: true });
  if (error) throw new Error(error.message);
  return ((data as { name: string }[] | null) ?? []).map((r) => r.name);
}

/** Nama merek aktif untuk pemilih tujuan link. */
export async function fetchPopupBrands(): Promise<string[]> {
  const db = createBrowserSupabase();
  if (!db) return [];
  const { data, error } = await db
    .from("product_brands")
    .select("name")
    .eq("is_active", true)
    .order("name", { ascending: true });
  if (error) throw new Error(error.message);
  return ((data as { name: string }[] | null) ?? []).map((r) => r.name);
}

/**
 * Upload gambar popup ke bucket publik `product-media` (folder `popups/`) —
 * mirror uploadTutorialImageToSupabase. Return public URL.
 */
export async function uploadPopupImage(file: File): Promise<string> {
  const db = createBrowserSupabase();
  if (!db) throw new Error("Tidak dapat terhubung.");
  const ext = file.name.split(".").pop() ?? "jpg";
  const path = await withStorePrefix(db, `popups/${Date.now()}.${ext}`);
  const { error } = await db.storage.from("product-media").upload(path, file, {
    upsert: false,
    contentType: file.type,
    cacheControl: "31536000",
  });
  if (error) throw new Error(error.message);
  const { data } = db.storage.from("product-media").getPublicUrl(path);
  return data.publicUrl;
}
